import React from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import { getStackedColumnsDateChartAxisTooltipAdapter, getStackedColumnsDateChartTooltipAdapter } from './tooltips';
import { StackedColumnsChart, StackedColumnsChartSeries, BaseStackedColumnsChartProps } from './stackedColumnsChart';
import './columnDateChart.css';
import { createYAxis, configureDragToZoom } from './lib';

/* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
export interface StackedColumnsDateChartSeries<Data extends { date: string } | { date: number }> extends StackedColumnsChartSeries<Data> {}

export interface IProps<Data extends { date: string } | { date: number }> extends
  BaseStackedColumnsChartProps<Data>
 {
  tooltipDateKey?: keyof Data,
  formatTooltipDate?: (date: number) => string,
  totalDescription?: string,
  formatData?: (dataForSeries: any, allData?: Data) => string,
  amchartsYAxisNumberFormat?: string,
  onlyShowCurrentSeriesInTooltip?: boolean,
  hideNullSeriesInTooltip?: boolean
}

export function StackedColumnsDateChart<Data extends { date: string } | { date: number }>(props: IProps<Data>) {

  const transformedData = props.data && props.data.length > 0 ? props.data.map(item => ({
    ...item,
    date: +item.date
  })) : [];

  const getTooltipAdapter = props.getTooltipAdapter ?? (({ seriesName }: { seriesName: string }) =>
    getStackedColumnsDateChartTooltipAdapter({
      seriesName,
      totalDescription: props.totalDescription,
      formatData: props.formatData,
      amchartsNumberFormat: props.amchartsYAxisNumberFormat,
      onlyShowCurrentSeries: props.onlyShowCurrentSeriesInTooltip,
      dateKey: props.tooltipDateKey,
      formatTooltipDate: props.formatTooltipDate
    }));

  const getAxisTooltipAdapter = props.getAxisTooltipAdapter ?? (() =>
    getStackedColumnsDateChartAxisTooltipAdapter({
      totalDescription: props.totalDescription,
      formatData: props.formatData,
      amchartsNumberFormat: props.amchartsYAxisNumberFormat,
      dateKey: props.tooltipDateKey,
      formatTooltipDate: props.formatTooltipDate,
      hideNullValues: props.hideNullSeriesInTooltip
    }));

  const provideXAxis = (chart: am4charts.XYChart) : am4charts.DateAxis => {
    const xAxis = chart.xAxes.push(new am4charts.DateAxis());
    xAxis.dataFields.date = 'date';
    xAxis.groupData = false;
    xAxis.renderer.grid.template.disabled = true;
    xAxis.renderer.minGridDistance = 50;

    if (!props.useAxisTooltip) {
      xAxis.cursorTooltipEnabled = false;
    }
    if (props.useAxisTooltip) {
      xAxis.dummyData = { allSeries: props.series };
      xAxis.adapter.add('getTooltipText', getAxisTooltipAdapter());
      xAxis.dummyData.allSeries = props.series;

      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineY.disabled = true;
      chart.cursor.lineX.disabled = true;
      chart.cursor.maxTooltipDistance = -1;
      chart.cursor.behavior = 'none';
    }

    if (props.dragToZoom) {
      configureDragToZoom(chart, xAxis);
    }

    return xAxis;
  };

  const provideYAxis = createYAxis(props.yAxisType, props.useAxisTooltip);

  return (
    <StackedColumnsChart<Data>
      {...props}
      data={transformedData}
      xAxisType={'date'}
      xFieldType={'dateX'}
      categoryKey={'date'}
      provideXAxis={provideXAxis}
      provideYAxis={provideYAxis}
      getTooltipAdapter={getTooltipAdapter}
    />
  );
}
